import React, { useEffect, useState } from "react";
import Select from "react-select";
import { getCustomerData } from "../service/customerService";
import { getMedicineData } from "../service/medicineService";
import { createOrder } from "../service/inventoryService";

const NewSales = () => {
  const lsUserVO = process.env.REACT_APP_LS_USER_VO;
  const pimsUserVO = JSON.parse(localStorage.getItem(lsUserVO));
  const [customer, setCustomer] = useState(null);
  const [isNewCustomer, setIsNewCustomer] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [medicineOptions, setMedicineOptions] = useState([]);
  const [newCustomer, setNewCustomer] = useState({
    name: "",
    address: "",
    contactNumber: "",
  });
  const [medicineItems, setMedicineItems] = useState([
    { medicineId: "", qty: "", mrp: "", finalPrice: "", totalPrice: 0 },
  ]);
  const [error, setError] = useState("");
  const [totalAmount, setTotalAmount] = useState(0);

  const fetchMedicineData = async () => {
    try {
      const medicineData = await getMedicineData(pimsUserVO.branchId);
      setMedicineOptions(medicineData);
    } catch (error) {
      console.error("Error fetching medicine data:", error);
    }
  };

  const fetchCustomerData = async () => {
    try {
      const customerData = await getCustomerData(pimsUserVO.orgId);
      setCustomers(customerData);
    } catch (error) {
      console.error("Error fetching customer data:", error);
    }
  };

  useEffect(() => {
    fetchMedicineData();
    fetchCustomerData();
  }, []);

  const handleAddItem = () => {
    setMedicineItems([
      ...medicineItems,
      { medicineId: "", qty: "", mrp: "", finalPrice: "", totalPrice: 0 },
    ]);
  };

  const handleRemoveItem = (index) => {
    const newItems = medicineItems.filter((_, itemIndex) => itemIndex !== index);
    setMedicineItems(newItems);
    calculateTotalAmount(newItems);
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...medicineItems];
    newItems[index] = {
      ...newItems[index],
      [field]: value,
    };
    if (field === "qty" || field === "finalPrice") {
      const qty = parseFloat(newItems[index].qty) || 0;
      const finalPrice = parseFloat(newItems[index].finalPrice) || 0;
      newItems[index].totalPrice = qty * finalPrice;
    }
    setMedicineItems(newItems);
    calculateTotalAmount(newItems);
  };

  const handleCustomerChange = (selectedOption) => {
    if (selectedOption.value === "New Customer") {
      setIsNewCustomer(true);
      setCustomer({ value: "New Customer", label: "New Customer" });
    } else {
      setIsNewCustomer(false);
      setCustomer(selectedOption);
    }
  };

  const handleNewCustomerChange = (field, value) => {
    setNewCustomer({ ...newCustomer, [field]: value });
  };

  const resetFields = () => {
    setCustomer(null);
    setIsNewCustomer(false);
    setNewCustomer({
      name: "",
      address: "",
      contactNumber: "",
    });
    setMedicineItems([{ medicineId: "", qty: "", mrp: "", finalPrice: "", totalPrice: 0 }]);
    setError("");
    setTotalAmount(0);
  };

  const calculateTotalAmount = (items) => {
    const total = items.reduce((acc, item) => acc + (item.totalPrice || 0), 0);
    setTotalAmount(total);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const processedCustomer = isNewCustomer ? newCustomer : customer;
    const hasMedicine = medicineItems.some((item) => item.medicineId !== "");

    if (isNewCustomer) {
      if (!newCustomer.name) {
        setError("Please enter a new customer name.");
        return;
      }
      if (!newCustomer.address) {
        setError("Please enter a new customer address.");
        return;
      }
      if (!newCustomer.contactNumber) {
        setError("Please enter a new customer contact number.");
        return;
      }
    } else if (!customer) {
      setError("Please select a customer.");
      return;
    }

    if (!hasMedicine) {
      setError("Please select at least one medicine.");
      return;
    }

    setError(""); // Clear any existing errors

    const orderDTO = {
      customerId: processedCustomer.value,
      newCustomer: isNewCustomer,
      customerDTO: isNewCustomer
        ? {
            name: newCustomer.name,
            address: newCustomer.address,
            contactNumber: newCustomer.contactNumber,
            customerType: "PATIENT",
            orgId: pimsUserVO.orgId,
          }
        : null,
      orderType: "SALE_ORDER",
      orderItemDTO: medicineItems,
      branchId: pimsUserVO.branchId,
    };

    try {
      const response = await createOrder(orderDTO);
      if (response && response.status === 201) {
        resetFields();
      } else {
        setError("Failed to create order. Please try again.");
      }
    } catch (error) {
      console.error("Error creating order:", error);
      setError("Failed to create order. Please try again.");
    }
  };

  const customStyles = {
    control: (base) => ({
      ...base,
      minHeight: 52, // Adjust the height here
    }),
  };

  return (
    <div className="container mx-auto p-4 sm:p-8">
      <div className="max-w-4xl mx-auto bg-green-200 p-6 sm:p-8 rounded-md shadow-md">
        <h2 className="text-2xl sm:text-3xl font-bold mb-4 sm:mb-6">Sale Order</h2>
        <form onSubmit={handleSubmit}>
          <div className="mb-4 sm:mb-6">
            <label className="block text-lg font-medium mb-2 sm:mb-3" htmlFor="customer">
              Customer
            </label>
            <Select
              className="w-full text-lg"
              value={customer}
              onChange={handleCustomerChange}
              options={[
                ...customers.map((cust) => ({
                  value: cust.id,
                  label: `${cust.name}, ${cust.address}, ${cust.contactNumber}`,
                })),
                { value: "New Customer", label: "New Customer" },
              ]}
              placeholder="Select Customer"
              isSearchable
              required
            />
            {isNewCustomer && (
              <div className="mt-2">
                <input
                  className="w-full p-2 sm:p-3 border border-gray-300 rounded-md text-lg mb-2"
                  type="text"
                  placeholder="New Customer Name"
                  value={newCustomer.name}
                  onChange={(e) => handleNewCustomerChange("name", e.target.value)}
                  required
                />
                <input
                  className="w-full p-2 sm:p-3 border border-gray-300 rounded-md text-lg mb-2"
                  type="text"
                  placeholder="New Customer Address"
                  value={newCustomer.address}
                  onChange={(e) => handleNewCustomerChange("address", e.target.value)}
                  required
                />
                <input
                  className="w-full p-2 sm:p-3 border border-gray-300 rounded-md text-lg"
                  type="text"
                  placeholder="Contact Number"
                  value={newCustomer.contactNumber}
                  onChange={(e) => handleNewCustomerChange("contactNumber", e.target.value)}
                  required
                />
              </div>
            )}
          </div>
          <h3 className="text-xl sm:text-2xl font-bold mb-2 sm:mb-4">Medicine Items</h3>
          {medicineItems.map((item, index) => (
            <div key={index} className="mb-4 sm:mb-6">
              <div className="flex flex-col sm:flex-row sm:space-x-3 mb-2 sm:mb-3">
                <Select
                  className="w-full sm:w-1/4 border border-gray-300 rounded-md text-lg mb-2 sm:mb-0"
                  styles={customStyles}
                  value={item.medicineId ? medicineOptions.find((opt) => opt.id === item.medicineId) : null}
                  onChange={(selectedOption) => handleItemChange(index, "medicineId", selectedOption ? selectedOption.id : "")}
                  options={medicineOptions}
                  getOptionLabel={(option) => option.medName}
                  getOptionValue={(option) => option.id}
                  placeholder="Select Medicine"
                  isSearchable
                  required
                />
                <input
                  className="w-full sm:w-1/6 p-2 sm:p-3 border border-gray-300 rounded-md text-lg mb-2 sm:mb-0"
                  type="number"
                  placeholder="Quantity"
                  value={item.qty}
                  onChange={(e) => handleItemChange(index, "qty", e.target.value)}
                  required
                />
                <input
                  className="w-full sm:w-1/6 p-2 sm:p-3 border border-gray-300 rounded-md text-lg mb-2 sm:mb-0"
                  type="number"
                  placeholder="MRP"
                  value={item.mrp}
                  onChange={(e) => handleItemChange(index, "mrp", e.target.value)}
                  required
                />
                <input
                  className="w-full sm:w-1/6 p-2 sm:p-3 border border-gray-300 rounded-md text-lg mb-2 sm:mb-0"
                  type="number"
                  placeholder="Final Price"
                  value={item.finalPrice}
                  onChange={(e) => handleItemChange(index, "finalPrice", e.target.value)}
                  required
                />
                <input
                  className="w-full sm:w-1/6 p-2 sm:p-3 border border-gray-300 rounded-md text-lg"
                  type="number"
                  placeholder="Total Price"
                  value={item.totalPrice.toFixed(2)}
                  readOnly
                />
                {medicineItems.length > 1 && (
                  <button
                    type="button"
                    className="p-2 sm:p-3 bg-red-500 text-white rounded-md text-lg mt-2 sm:mt-0"
                    onClick={() => handleRemoveItem(index)}
                  >
                    Remove
                  </button>
                )}
              </div>
              {index === medicineItems.length - 1 && (
                <button
                  type="button"
                  className="w-full p-2 sm:p-3 bg-green-500 text-white rounded-md text-lg"
                  onClick={handleAddItem}
                >
                  Add Medicine
                </button>
              )}
            </div>
          ))}
          <div className="mb-4 sm:mb-6 text-xl sm:text-2xl font-bold">
            Total Amount: ${totalAmount.toFixed(2)}
          </div>
          {error && (
            <div className="mb-4 sm:mb-6 text-red-600 text-lg font-medium">
              {error}
            </div>
          )}
          <button
            className="w-full bg-green-700 text-white px-4 py-2 sm:py-3 rounded-md text-lg hover:bg-blue-600"
            type="submit"
          >
            Submit
          </button>
        </form>
      </div>
    </div>
  );
};

export default NewSales;
